import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import { faBarsFilter } from "@fortawesome/pro-solid-svg-icons/faBarsFilter";
import { faBuilding } from "@fortawesome/pro-solid-svg-icons/faBuilding";
import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { faSitemap } from "@fortawesome/pro-solid-svg-icons/faSitemap";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { faUser } from "@fortawesome/pro-solid-svg-icons/faUser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import { PermissionName } from "@mapmycustomers/shared";
import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import { Deal, EntityType } from "@mapmycustomers/shared/types/entity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import User from "@mapmycustomers/shared/types/User";

import { ActionBarItemType } from "@app/component/ActionsBar";
import { updateDeal } from "@app/store/deal/actions";
import { hasPermission } from "@app/store/iam";
import useClickTrackerContext from "@app/util/contexts/useClickTrackerContext";
import useEntityLinkGetter from "@app/util/contexts/useEntityLinkGetter";
import isClosedStage from "@app/util/isClosedStage";
import { dealLayoutModel } from "@app/util/layout/impl";

import Actions from "./Actions";
import messages from "./messages";

const useDealActions = (
  currentUser: User,
  entity: Deal,
  funnelStages: Record<Funnel["id"], Stage[]>,
  handleQuickActionButtonClick: (info: MenuInfo) => void,
  onUpdateDeal: typeof updateDeal.request,
  onEditStageDeal: (deal: Deal | undefined) => void
) => {
  const intl = useIntl();
  const getEntityLink = useEntityLinkGetter();
  const stages = useMemo(() => funnelStages[entity.funnel?.id] ?? [], [entity, funnelStages]);
  const clickTrackerContext = useClickTrackerContext();

  const handleMarkWon = useCallback(() => {
    clickTrackerContext.click(Actions.MARK_WON);
    const wonStage = stages.find(({ type }) => type === DealStageType.WON);
    if (!wonStage) {
      return;
    }
    const updatedDeal = { ...(entity as Deal), stage: wonStage };
    if (dealLayoutModel.hasMissingRequiredFieldsFor(updatedDeal)) {
      onEditStageDeal(updatedDeal);
    } else {
      onUpdateDeal(updatedDeal);
    }
  }, [clickTrackerContext, entity, onUpdateDeal, onEditStageDeal, stages]);

  const handleMarkLost = useCallback(() => {
    clickTrackerContext.click(Actions.MARK_LOSS);
    const lostStage = stages.find(({ type }) => type === DealStageType.LOST);
    if (!lostStage) {
      return;
    }
    const updatedDeal = { ...(entity as Deal), stage: lostStage };
    if (dealLayoutModel.hasMissingRequiredFieldsFor(updatedDeal)) {
      onEditStageDeal(updatedDeal);
    } else {
      onUpdateDeal(updatedDeal);
    }
  }, [clickTrackerContext, entity, onUpdateDeal, onEditStageDeal, stages]);

  const handleOpenCompany = useCallback(() => {
    clickTrackerContext.click(Actions.OPEN_COMPANY);
    window.open(
      !entity.account?.id ? undefined : getEntityLink(EntityType.COMPANY, entity.account!.id),
      "_blank"
    );
  }, [clickTrackerContext, entity, getEntityLink]);

  const handleOpenPerson = useCallback(() => {
    clickTrackerContext.click(Actions.OPEN_PERSON);
    window.open(
      !entity.contact?.id ? undefined : getEntityLink(EntityType.PERSON, entity.contact!.id),
      "_blank"
    );
  }, [clickTrackerContext, entity, getEntityLink]);

  const canUpdateDealGlobally = useSelector(hasPermission)(PermissionName.DEAL_UPDATE);
  // Per-record access gates editing; fall back to the global permission when accessStatus is absent.
  const canUpdateDeal = entity.accessStatus?.update ?? canUpdateDealGlobally;

  const dealActions: ActionBarItemType[] = useMemo(
    () => [
      {
        disabled: !canUpdateDeal,
        icon: <FontAwesomeIcon icon={faBarsFilter} />,
        key: Actions.EDIT_STAGE,
        label: intl.formatMessage(messages.editStage),
        onClick: handleQuickActionButtonClick,
      },
      {
        disabled:
          !(entity as Deal)?.stage || isClosedStage((entity as Deal).stage) || !canUpdateDeal,
        icon: <FontAwesomeIcon icon={faCheck} />,
        key: Actions.MARK_WON,
        label: intl.formatMessage(messages.markWon),
        onClick: handleMarkWon,
      },
      {
        disabled:
          !(entity as Deal)?.stage || isClosedStage((entity as Deal).stage) || !canUpdateDeal,
        icon: <FontAwesomeIcon icon={faTimes} />,
        key: Actions.MARK_LOSS,
        label: intl.formatMessage(messages.markLost),
        onClick: handleMarkLost,
      },
      {
        disabled: !entity.account?.id,
        icon: <FontAwesomeIcon icon={faBuilding} />,
        key: Actions.OPEN_COMPANY,
        label: intl.formatMessage(messages.openCompany),
        onClick: handleOpenCompany,
      },
      {
        disabled: !entity.contact?.id,
        icon: <FontAwesomeIcon icon={faUser} />,
        key: Actions.OPEN_PERSON,
        label: intl.formatMessage(messages.openPerson),
        onClick: handleOpenPerson,
      },
      {
        children: [
          {
            key: Actions.MANAGE_PARENT_COMPANY,
            label: intl.formatMessage(messages.manageCompany),
            onClick: handleQuickActionButtonClick,
          },
          {
            key: Actions.MANAGE_PERSON,
            label: intl.formatMessage(messages.managePerson),
            onClick: handleQuickActionButtonClick,
          },
        ],
        disabled: !canUpdateDeal,
        icon: <FontAwesomeIcon icon={faSitemap} />,
        key: Actions.MANAGE_RELATIONSHIP,
        label: intl.formatMessage(messages.manageRelationships),
      },
    ],
    [
      canUpdateDeal,
      entity,
      handleMarkLost,
      handleMarkWon,
      handleOpenCompany,
      handleOpenPerson,
      handleQuickActionButtonClick,
      intl,
    ]
  );

  return dealActions;
};

export default useDealActions;
